import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearLoadMoreError, loadMoreProducts, setLayoutChanging } from '../../redux/slices/filterSlice';
import LoadingSpinner from '../ui/LoadingSpinner';
// Status Components


export const ErrorMessage = ({ message }) => (
  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded mb-4">
    <p className="text-red-700 font-medium">Error Loading Products</p>
    <p className="text-red-600">{message || 'Please try again later.'}</p>
  </div>
);

export const NoResultsMessage = ({ searchTerm }) => (
  <div className="bg-brand-beige/10 border-l-4 border-brand-beige p-4 rounded mb-4">
    <p className="text-brand-orange font-medium">No Products Found</p>
    <p className="text-brand-orange-dark">
      {searchTerm
        ? `No results found for "${searchTerm}". Try different search terms.`
        : 'Try adjusting your filters to see more results.'}
    </p>
  </div>
);

export const EndOfResultsMessage = () => (
  <div className="text-center py-4 mt-4 border-t border-gray-200">
    <p className="text-gray-500 text-sm">End of Results</p>
  </div>
);

// How far below the fold the sentinel may sit and still trigger a fetch.
const LOAD_MORE_MARGIN_PX = 600;

export const LoadMoreError = ({ message, onRetry }) => (
  <div className="flex flex-col items-center gap-2 py-8">
    <p className="text-sm text-gray-600">{message || 'Could not load more products.'}</p>
    <button
      type="button"
      onClick={onRetry}
      className="px-4 py-2 text-sm font-medium rounded border border-brand-beige text-brand-orange hover:bg-brand-beige/10"
    >
      Try again
    </button>
  </div>
);

const CardsContainer = ({
  children,
  className = '',
  loading,
  error,
  hasMore,
  totalResults,
  searchTerm,
  dataLoading,
  isInitialized,
  lastSearch
}) => {
  const dispatch = useDispatch();
  const layoutChanging = useSelector(state => state.filter.layoutChanging);
  const pageType = useSelector(state => state.filter.pageType);
  const loadingMore = useSelector(state => state.filter.loadingMore);
  const loadMoreError = useSelector(state => state.filter.loadMoreError);
  const sentinelRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const resizeTimerRef = useRef(null);
  const childrenArray = React.Children.toArray(children);
  const hasChildren = childrenArray.length > 0;

  // Reset state when component mounts/unmounts or page type changes
  useEffect(() => {
    dispatch(setLayoutChanging(false));

    return () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      dispatch(setLayoutChanging(false));
    };
  }, [dispatch, pageType]);

  // Detect window resize
  useEffect(() => {
    const handleResize = () => {
      setIsResizing(true);
      dispatch(setLayoutChanging(true));

      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }

      resizeTimerRef.current = setTimeout(() => {
        setIsResizing(false);
        dispatch(setLayoutChanging(false));
      }, 1000);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
        dispatch(setLayoutChanging(false));
      }
    };
  }, [dispatch]);

  // A fresh search (new term, sort or filter) replaces the whole result set, so
  // send the user back to the top of it. Staying parked deep in the old list
  // shows a viewport of unrelated rows and makes the grid immediately page
  // itself back down to that depth just to fill the space.
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (loading && !wasLoadingRef.current && window.scrollY > 0) {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    wasLoadingRef.current = loading;
  }, [loading]);

  // Latest gating values, read by maybeLoadMore. Keeping them in a ref lets that
  // callback stay referentially stable, so the listeners below attach once and
  // never see a stale closure.
  const gatesRef = useRef({});
  gatesRef.current = { hasMore, loading, dataLoading, loadingMore, isResizing, layoutChanging, loadMoreError };

  // The single decision point for "should another page be fetched right now?".
  // It measures the sentinel live instead of trusting a cached flag: an
  // IntersectionObserver only reports boundary *crossings*, so its last answer
  // can be both stale (a just-appended page already pushed the sentinel far
  // below the fold) and unrefreshable (scrolling back down re-reports the same
  // `true` it already holds, which React treats as no change — the dead end
  // where the grid simply stops loading until you scroll away and back).
  const maybeLoadMore = useCallback(() => {
    const gates = gatesRef.current;
    if (
      !gates.hasMore ||
      gates.loading ||
      gates.dataLoading ||
      gates.loadingMore ||
      gates.isResizing ||
      gates.layoutChanging ||
      gates.loadMoreError
    ) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const { top } = sentinel.getBoundingClientRect();
    if (top > window.innerHeight + LOAD_MORE_MARGIN_PX) return;

    dispatch(loadMoreProducts());
  }, [dispatch]);

  // Trigger 1 — the sentinel entering the pre-load zone.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[entries.length - 1].isIntersecting) maybeLoadMore();
      },
      {
        root: null, // observe against the viewport
        rootMargin: `${LOAD_MORE_MARGIN_PX}px 0px`, // start fetching before the user hits the end
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasChildren, maybeLoadMore]);

  // Trigger 2 — scrolling. Capture phase so it also catches scrolls inside a
  // nested scroll container, coalesced to one check per frame. This is the
  // backstop for every case the observer cannot see: a crossing that happened
  // while the tab was in the background, a layout shift that moved the sentinel
  // without any scrolling, or the user scrolling back down after the observer
  // has already latched on to "intersecting".
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        maybeLoadMore();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [maybeLoadMore]);

  // Trigger 3 — a page finished loading (or a gate cleared). Covers the case
  // where the freshly appended rows still don't reach past the fold: without it
  // a short page, or a viewport taller than one page, would stall until the
  // user scrolled, and there'd be nothing left to scroll.
  useEffect(() => {
    maybeLoadMore();
  }, [
    maybeLoadMore,
    hasMore,
    loading,
    dataLoading,
    loadingMore,
    isResizing,
    layoutChanging,
    loadMoreError,
    childrenArray.length
  ]);

  const handleRetryLoadMore = useCallback(() => {
    dispatch(clearLoadMoreError());
    dispatch(loadMoreProducts());
  }, [dispatch]);

  const renderContent = () => {
    // Full-height spinner only when there is nothing to show yet. Once cards are
    // on screen they stay mounted — swapping them for a spinner unmounts every
    // card, collapses the page height and throws the scroll position back to the
    // top, which is exactly the "whole grid reloaded" flash.
    if ((loading && !hasChildren) || (!isInitialized && !lastSearch && !hasChildren)) {
      return (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner />
        </div>
      );
    }

    // Show error if we have one and no products
    if (error && !loading && childrenArray.length === 0) {
      return <ErrorMessage message={error} />;
    }

    // Show products grid if we have products
    if (hasChildren) {
      return (
        <div className="px-2 sm:px-4 py-3">
          {searchTerm && totalResults > 0 && (
            <div className="mb-3 text-sm text-gray-600">
              Found {totalResults} results for "{searchTerm}"
            </div>
          )}

          <div
            className={`grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4 transition-opacity duration-200 ease-in-out ${
              loading ? 'opacity-50' : 'opacity-100'
            }`}
          >
            {children}
          </div>

          {/* Sentinel — IntersectionObserver watches this to trigger load more.
              Kept mounted whenever there are cards (even mid-fetch) so its
              visibility stays accurate across pages. */}
          <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />

          {/* Inline spinner beneath the grid while the next page loads */}
          {loadingMore && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <LoadingSpinner />
              <p className="text-xs text-gray-500">Loading more…</p>
            </div>
          )}

          {loadMoreError && !loadingMore && (
            <LoadMoreError message={loadMoreError} onRetry={handleRetryLoadMore} />
          )}

          {isInitialized && lastSearch && !hasMore && !loading && !loadingMore && !loadMoreError && (
            <EndOfResultsMessage />
          )}
        </div>
      );
    }

    // Only show no results if we're initialized and have made at least one search
    if (isInitialized && lastSearch && !loading && !dataLoading) {
      return <NoResultsMessage searchTerm={searchTerm} />;
    }

    // Fallback loading state
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  };

  return (
    <div className={className}>
      {renderContent()}
    </div>
  );
};

export default CardsContainer; 